<template>
<div :class="[$setClasses, cardClass]">
  <card-flex class="payment"
             iconSize="2x" :icon="iconName">
    <div class="payment__content" v-html="$t('profile.payment.infos', {
      number: $helpers.pathText.getTextVersion('profile.payment.number', {number: card.last4 })})"></div>
    <template #after v-if="!hiddenAfter">
      <el-tag v-if="isCardExpired"
              color="var(--nxt-color-danger)"
              effect="dark">
        {{ tagText }}</el-tag>
      <el-tag v-else-if="card.is_default && !isMobile"
              effect="dark">
        {{ tagText }}</el-tag>
    </template>
  </card-flex>
  <div :class="$vClass('select-default')"
       :hidden="hiddenAfter || isCardExpired">
    <el-button link
               size="small"
               type=""
               @click="!card.is_default ? updateCard() : null">
      <div :class="[$vClass('circle'), card.is_default ? '--default' : '']"></div>
    </el-button>
  </div>
  <div :class="$vClass('delete')"
       :hidden="hiddenDelete">
    <el-button :disabled="card.is_default"
               link
               size="small"
               type=""
               @click="removeCard">
      <font-awesome-icon :icon="['fas', 'trash']"/></el-button>
  </div>
</div>
</template>

<script>
import { mapActions, mapState, mapWritableState } from 'pinia';
import { useDimensionStore, useProfileStore } from '@/stores';
import CardFlex from '@/utils/components/CardFlex.vue';

export default {
  name: 'DetailsCardPayment',
  nameClassStyle: 'details-card-payment',
  components: {
    CardFlex,
  },
  props: {
    card: Object,
    hiddenAfter: Boolean,
    hiddenDelete: Boolean,
  },
  computed: {
    ...mapState(useDimensionStore, ['isMobile']),
    ...mapWritableState(useProfileStore, ['cardLoading']),
    iconName() {
      const icon = this.listIcon[this.card.brand];
      return icon ? ['fab', `cc-${icon}`] : ['fas', 'credit-card'];
    },
    isCardExpired() {
      const today = new Date();
      const currentMonth = today.getMonth() + 1;
      const currentYear = today.getFullYear();
      const year = this.card.exp_year;
      const month = this.card.exp_month;
      return year < currentYear || (year === currentYear && month < currentMonth);
    },
    tagText() {
      let pathText = 'profile.payment.inactive';
      if (this.isCardExpired) {
        pathText = 'profile.payment.expiry';
      } else if (this.card.is_default) {
        pathText = 'profile.payment.active';
      }
      return this.$helpers.pathText.getTextVersion(pathText, { isMobile: this.isMobile });
    },
    cardClass() {
      if (this.isCardExpired) {
        return '--expired';
      }
      if (this.card.is_default) {
        return '--active';
      }
      return '--inactive';
    },
  },
  data() {
    return {
      listIcon: {
        visa: 'visa',
        mastercard: 'mastercard',
        amex: 'amex',
      },
    };
  },
  methods: {
    ...mapActions(useProfileStore, ['getCardsPayment', 'updateCardPayment', 'setDeleteCardPaymentModalDisplayed']),
    formatDateExpire(month, year) {
      return this.$helpers.demand.formatDateString(false, { year, month });
    },
    removeCard() {
      this.setDeleteCardPaymentModalDisplayed(this.card);
    },
    async updateCard() {
      try {
        this.cardLoading = true;
        await this.updateCardPayment({
          pm_id: this.card.pm_id,
        });
        await this.getCardsPayment();
        // eslint-disable-next-line no-empty
      } catch (error) {
        this.cardLoading = false;
      } finally {
        this.displayAddPayment = false;
        this.cardLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.--margin-right {
  margin-right: 10px;
}
.--uppercase {
  text-transform: uppercase;
}

.details-card-payment {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: start;
  background-color: #e9ebf1;
  border-radius: var(--nxt-border-radius-base);

  :deep(.el-tag) {
    border: none;
    opacity: 0.6;
  }

  &__select-default, &__delete {
    height: 100%;

    button {
      height: 100%;
      box-shadow: none;
      padding: var(--nxt-space-default);
    }
  }

  &__select-default {
    background-color: #e9ebf1;
  }

  &__delete {
    background-color: var(--nxt-color-gainsboro);
    border-top-right-radius: var(--nxt-border-radius-base);
    border-bottom-right-radius: var(--nxt-border-radius-base);
  }

  &.--expired {
    :deep(.el-tag) {
      opacity: 0.5;
    }

    @include breakpoint(tablet) {
      margin-right: 50.5px;
    }
  }

  &__circle {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid var(--nxt-color-primary);

    &.--default {
      border: 4px solid var(--nxt-color-primary);
    }
  }
}

.payment {
  flex: 1;

  &.card-flex {
    margin-bottom: 0;
    height: 100%;
    background-color: var(--nxt-color-light-grey);
    border-radius: var(--nxt-border-radius-base);
    border: 0;

    :deep(.el-card__body) {
      padding: var(--nxt-space-small);

      @include breakpoint(tablet) {
        padding: var(--nxt-space-small) var(--nxt-space-medium);
      }
    }

    :deep(.card-flex__wrapper)  {
      flex-wrap: wrap;
    }

    :deep(.card-flex__content) {
      text-align: initial;
      font-size: var(--nxt-font-size-default);
    }

    :deep(.card-flex__after) {
      display: flex;
      justify-content: flex-end;
    }

    @include breakpoint(mobile) {
      :deep(.card-flex__wrapper)  {
        flex-wrap: nowrap;
      }

      :deep(.card-flex__after) {
        width: auto;
        display: block;
        margin-top: 0;
      }
    }
  }
}
</style>
